import React from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface PageLoaderProps {
  type?: 'default' | 'table' | 'dashboard';
  title?: string;
  showSidebar?: boolean;
}

export const PageLoader: React.FC<PageLoaderProps> = ({ 
  type = 'default', 
  title = 'Đang tải...',
  showSidebar = false 
}) => {
  if (type === 'dashboard') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto flex h-[calc(100vh-140px)]">
          {/* Sidebar skeleton */}
          {showSidebar && (
            <div className="w-80 border-r bg-white p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-8 w-20" />
                </div>
                
                {/* Group items skeleton */}
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-3">
                    <Skeleton className="h-5 w-5 rounded" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-24 mb-1" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    <Skeleton className="h-5 w-8 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Main content skeleton */}
          <div className="flex-1 p-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-6 w-48" />
                  </div>
                  <Skeleton className="h-9 w-64" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Table header */}
                  <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-18" />
                  </div>
                  
                  {/* Table rows */}
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="grid grid-cols-4 gap-4 p-4 border-b">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-16" />
                      <div className="flex gap-2">
                        <Skeleton className="h-7 w-12" />
                        <Skeleton className="h-7 w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'table') {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          {/* Page header skeleton */}
          <div className="mb-8">
            <Skeleton className="h-10 w-64 mb-2" />
            <Skeleton className="h-6 w-96 mb-4" />
            <div className="flex gap-4">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-5 w-24" />
            </div>
          </div>

          {/* Card skeleton */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-5" />
                  <Skeleton className="h-6 w-40" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-24" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                {/* Table header */}
                <div className="grid grid-cols-6 gap-4 p-4 bg-gray-50 border-b">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-4 w-20" />
                  ))}
                </div>
                
                {/* Table rows */}
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="grid grid-cols-6 gap-4 p-4 border-b">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div>
                        <Skeleton className="h-4 w-24 mb-1" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                    <div>
                      <Skeleton className="h-4 w-32 mb-1" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-5 w-16 rounded" />
                    <div className="flex flex-wrap gap-1">
                      <Skeleton className="h-4 w-12 rounded" />
                      <Skeleton className="h-4 w-16 rounded" />
                    </div>
                    <Skeleton className="h-5 w-14 rounded" />
                    <div className="flex gap-1">
                      <Skeleton className="h-7 w-12" />
                      <Skeleton className="h-7 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Default loading
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center max-w-sm w-full p-6">
        <div className="relative mb-6">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-6 w-6 bg-blue-600 rounded-full animate-pulse"></div>
          </div>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-500 mb-4">Vui lòng chờ trong giây lát...</p>
        
        <div className="space-y-2">
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-2 w-3/4 mx-auto" />
          <Skeleton className="h-2 w-1/2 mx-auto" />
        </div>
        
        <div className="mt-6 text-xs text-gray-400 space-y-1">
          <div className="flex justify-between">
            <span>Loading:</span>
            <span className="text-blue-600">Đang tải...</span>
          </div>
          <div className="flex justify-between">
            <span>Status:</span>
            <span className="text-green-600">Kết nối thành công</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLoader; 